import { FC, useMemo } from 'react';

import { LoadingSpinner } from '../../../../@paco/components';
import { ConnectedHelpTooltip } from '../../../../@paco/connectors';
import { Exchange, ShiftExchange } from '../../../../@paco/entities/Exchange/Exchange';
import { Permission } from '../../../../@paco/entities/Permission/Permission';
import checkPermission from '../../../../@paco/helpers/permissions/checkPermission';
import trans from '../../../../@paco/helpers/trans';
import ExchangeRequestCard from '../../../../components/ExchangeRequestCard/ExchangeRequestCard';
import ExchangeShiftCard from '../../../../components/ExchangeShiftCard/ExchangeShiftCard';
import NewFilters from '../../../../components/Filters/Filters';
import SearchBar from '../../../../components/SearchBar/SearchBar';
import SidebarPage from '../../../../components/SidebarPage/SidebarPage';
import { translate } from '../../../../helpers/translations/translator';
import { AppFilter } from '../../../../redux/filter-ts/filterModels';

import './ExchangeRequestsContainer.scss';

interface ExchangeRequestsContainerProps {
    isLoading: boolean;
    exchanges: (ShiftExchange | Exchange)[];
    permissions: Permission[];
    filter: AppFilter,
    onApproveExchange: (exchangeId: string, userId: string) => void;
    onDeclineExchange: (exchangeId: string) => void;
    onApproveShiftExchange: (exchangeId: string) => void;
    onDeclineShiftExchange: (exchangeId: string) => void;
    onSearchBarChange: (search: string) => void;
    onExchangeNavButtonClick: (startDate: Date) => void;
    onFilterChange: (filter: AppFilter) => void;
}

const ExchangeRequestsContainer: FC<ExchangeRequestsContainerProps> = ({
    isLoading,
    exchanges,
    permissions,
    filter,
    onApproveExchange,
    onDeclineExchange,
    onApproveShiftExchange,
    onDeclineShiftExchange,
    onSearchBarChange,
    onExchangeNavButtonClick,
    onFilterChange,
}) => {
    const canEditExchanges = useMemo(() => checkPermission(permissions, 'edit-own-departments-exchanges'), [permissions]);

    return (
        <SidebarPage
            sidebarContent={(
                <NewFilters
                    filter={filter}
                    filterBlocks={[
                        'exchanges',
                    ]}
                    onChange={onFilterChange}
                />
            )}
        >
            <div className="exchange-requests">
                <div className="absences-search">
                    <SearchBar placeholder={translate('common.searchEmployee')} startVal={filter.employeeSearch} onChange={onSearchBarChange} />
                </div>

                <div className="exchange-requests__cards">
                    {exchanges.map(exchange => {
                        if (exchange.type === 'shift_exchanges') {
                            return (
                                <ExchangeShiftCard
                                    key={exchange.id}
                                    className="exchange-requests__card"
                                    onApproveClick={canEditExchanges ? onApproveShiftExchange : undefined}
                                    onDeclineClick={canEditExchanges ? onDeclineShiftExchange : undefined}
                                    shiftExchange={exchange}
                                />
                            );
                        }

                        return (
                            <ExchangeRequestCard
                                className="exchange-requests__card"
                                key={exchange.id}
                                request={exchange}
                                onApproveClick={canEditExchanges ? onApproveExchange : undefined}
                                onDeclineClick={canEditExchanges ? onDeclineExchange : undefined}
                                onCalendarClick={() => onExchangeNavButtonClick(exchange.shift?.period.start || new Date())}
                            />
                        );
                    })}
                </div>
            </div>

            {isLoading && <LoadingSpinner />}

            <ConnectedHelpTooltip
                index={0}
                route="exchange-requests"
                subTitle={trans('help.absences.exchangeItem.title')}
                text={trans('help.absences.exchangeItem.text')}
                title={trans('help.absences.title')}
                className="exchange-requests__exchange-item-help-tooltip"
            />

            <ConnectedHelpTooltip
                index={1}
                route="exchange-requests"
                subTitle={trans('help.absences.leaveOfAbsencesFilters.title')}
                text={trans('help.absences.leaveOfAbsencesFilters.text')}
                title={trans('help.absences.title')}
                showMobileInfoWarning
                className="exchange-requests__filters-help-tooltip"
            />
        </SidebarPage>
    );
};

export default ExchangeRequestsContainer;
